import type { ReactNode } from "react";
import { Link } from "react-router";
import { ApiError } from "../api/errors";
import { formatLocal } from "../lib/datetime";

// The facts a typed rejection's `detail` carries (G-22), spelled out under
// its message. Organization (0062) and dispute (0063) refusals put these
// fields in `detail` (campaign_commands.go:236-244 writeCampaignError):
//
//   required_roles         insufficient_role / insufficient_authority
//   active_at              authority_not_yet_active
//   request_id             approval_quorum_would_break, role_change_pending
//   dispute_eligible_until dispute_window_closed
//   dispute_id             line_already_disputed
//   max_bytes / cap_bytes  evidence_too_large / evidence_storage_cap_exceeded
//   media_type             evidence_type_not_allowed (the SNIFFED type)
//   reservation_state      reservation_not_disputable

const ROLE_WORD: Record<string, string> = {
  owner: "Owner",
  admin: "Admin",
  payout_approver: "Payout approver",
  spend_approver: "Spend approver",
  editor: "Editor",
  member: "Member",
  platform_reviewer: "VADS reviewer",
};

function mb(bytes: unknown): string | null {
  return typeof bytes === "number" ? `${Math.round((bytes / 1048576) * 10) / 10} MB` : null;
}

export function RejectionDetail({ error, home }: { error: unknown; home: string }) {
  if (!(error instanceof ApiError) || !error.detail) return null;
  const d = error.detail;
  const items: ReactNode[] = [];
  const roles = Array.isArray(d.required_roles) ? d.required_roles.filter((r): r is string => typeof r === "string") : [];
  if (roles.length) items.push(<span key="roles">Needs the role: {roles.map((r) => ROLE_WORD[r] ?? r).join(" or ")}.</span>);
  const activeAt = error.detailString("active_at");
  if (activeAt) items.push(<span key="active">Your authority becomes usable at {formatLocal(activeAt)}.</span>);
  const requestId = error.detailString("request_id");
  if (requestId && (error.code === "approval_quorum_would_break" || error.code === "role_change_pending")) {
    items.push(
      <span key="req">
        <Link to={`${home}/org/approvals`}>See the pending request</Link>
      </span>,
    );
  }
  const until = error.detailString("dispute_eligible_until");
  if (until) items.push(<span key="until">The window closed at {formatLocal(until)}.</span>);
  const disputeId = error.detailString("dispute_id");
  if (disputeId) {
    items.push(
      <span key="dispute">
        <Link to={`${home}/disputes/${disputeId}`}>Open that dispute</Link>
      </span>,
    );
  }
  const maxBytes = mb(d.max_bytes);
  if (maxBytes && error.code === "evidence_too_large") items.push(<span key="max">Limit: {maxBytes} per file.</span>);
  const cap = mb(d.cap_bytes);
  const used = mb(d.used_bytes);
  if (cap) items.push(<span key="cap">Used {used ?? "?"} of {cap}.</span>);
  const media = error.detailString("media_type");
  if (media && error.code === "evidence_type_not_allowed") items.push(<span key="media">The file's contents look like {media}.</span>);
  const resState = error.detailString("reservation_state");
  if (resState) items.push(<span key="res">The delivery is now {resState.replace(/_/g, " ")}.</span>);
  if (!items.length) return null;
  return <div className="stack-sm small">{items}</div>;
}
